/**
 * 展立宏编码（移植 demo libs/macro.getMacroData）
 */
import type { SdkMacroAction } from "../macro";

export const ZHANLI_MACRO_DEVICE_SLOTS = 4;

function shiftFrom16Bit(value: number): [number, number] {
  const v = Math.max(0, Math.min(0xffff, Math.trunc(value) || 0));
  return [v & 0xff, (v >> 8) & 0xff];
}

/** Hub 鼠标键值 1–5 → demo evtToAction.button */
const HUB_MOUSE_TO_DEMO: Record<number, number> = {
  1: 0,
  2: 2,
  3: 1,
  4: 4,
  5: 3,
};

/** 录制 nativeKey → USB HID 用法。已存的位掩码（Alt=0x04）靠 nativeKey 纠正，避免误伤字母 A */
const MODIFIER_NATIVE_TO_HID: Record<string, number> = {
  ControlLeft: 0xe0,
  ShiftLeft: 0xe1,
  AltLeft: 0xe2,
  MetaLeft: 0xe3,
  OSLeft: 0xe3,
  ControlRight: 0xe4,
  ShiftRight: 0xe5,
  AltRight: 0xe6,
  MetaRight: 0xe7,
  OSRight: 0xe7,
};

function keyboardValueToDemoCode(value: number, nativeKey?: string): number {
  if (nativeKey && MODIFIER_NATIVE_TO_HID[nativeKey] != null) {
    return MODIFIER_NATIVE_TO_HID[nativeKey];
  }
  const usage = Math.trunc(value);
  if (usage >= 0 && usage <= 0xff) return usage & 0xff;
  return usage & 0xff;
}

/** demo getMacroData：0左 1中 2右 3后退 4前进 → HID 0x01/0x04/0x02/0x08/0x10 */
function demoMouseCodeToProtocol(code: number): number {
  const map: Record<number, number> = {
    0: 0x01,
    1: 0x04,
    2: 0x02,
    3: 0x08,
    4: 0x10,
  };
  return map[code] ?? code & 0xff;
}

/** 将单条宏动作编码为展立协议 4 字节步（不含 slot 头） */
export function encodeZhanliMacroActions(actions: SdkMacroAction[]): number[] {
  const bytes: number[] = [];
  const actionOffset = 1;
  bytes[0] = 0;

  actions.forEach((item, index) => {
    let action = 0;
    let code = 0;
    const isRelease = Number(item.status) === 1;
    if (isRelease) action |= 1 << 7;

    if (item.type === "mouse") {
      action |= 0x10;
      const demoBtn =
        HUB_MOUSE_TO_DEMO[Number(item.value)] ??
        Math.max(0, Number(item.value) - 1);
      code = demoMouseCodeToProtocol(demoBtn);
    } else {
      action |= 0x30;
      code = keyboardValueToDemoCode(
        Number(item.value),
        (item as { nativeKey?: string }).nativeKey
      );
    }

    const [lo, hi] = shiftFrom16Bit(Number(item.delay) || 0);
    const base = actionOffset + index * 4;
    bytes[base] = action;
    bytes[base + 1] = lo;
    bytes[base + 2] = hi;
    bytes[base + 3] = code;
  });

  return bytes;
}

/** 设备宏槽 slotIndex(0~3) + 动作列表 → setMouseMacro 载荷 */
export function encodeZhanliMacroSlot(
  slotIndex: number,
  actions: SdkMacroAction[]
): number[] {
  const body = encodeZhanliMacroActions(actions);
  body[0] = slotIndex & 0xff;
  return body;
}

/**
 * setKeyFunction.param：[code1, code2, macroProfileIdx]
 * 与 demo 最终下发一致（槽位与宏索引 0~3 一一对应）：
 * - code1 = slot | (loopType << 4)  —— 低 4 位为设备宏槽（固件只读这个）
 * - code2 = 循环次数（loopType=2 时）
 * - code3 = 宏索引（仅软件侧；批量 setMouseKeys 不会写入设备）
 */
export function zhanliMacroKeyParam(
  macroIdx: number,
  macro?: { macroMode?: number; cycle?: number }
): [number, number, number] {
  const slot = Math.max(
    0,
    Math.min(ZHANLI_MACRO_DEVICE_SLOTS - 1, Math.trunc(macroIdx) || 0)
  );
  const raw = Number(macro?.macroMode);
  const loopType = [1, 2, 4].includes(raw) ? raw : 2;
  const loopTimes =
    loopType === 2
      ? Math.min(255, Math.max(1, Number(macro?.cycle ?? 1) || 1))
      : 1;
  return [
    (slot & 0x0f) | ((loopType << 4) & 0xf0),
    loopTimes & 0xff,
    slot & 0xff,
  ];
}
